
import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { useSearch } from "@/contexts/search/SearchContext";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

const SearchInput = () => {
  const { 
    searchQuery, 
    performSearch, 
    searchResults, 
    isSearching, 
    isSearchOpen, 
    setIsSearchOpen,
    trackSearchResultClick 
  } = useSearch();
  
  const [localQuery, setLocalQuery] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && e.target !== inputRef.current) {
        e.preventDefault();
        setIsSearchOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        setLocalQuery("");
        performSearch("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setIsSearchOpen, performSearch]);

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localQuery !== searchQuery) {
        performSearch(localQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localQuery, searchQuery, performSearch]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsSearchOpen]);

  const handleResultClick = (result: any) => {
    // Track the search result click
    trackSearchResultClick(result);
    
    // Navigate to the content
    navigate(`/content/${result.id}`);
    setIsSearchOpen(false);
    setLocalQuery("");
    performSearch("");
  };

  const clearSearch = () => {
    setLocalQuery("");
    performSearch("");
    setIsSearchOpen(false);
  };

  return (
    <div className="relative w-full max-w-sm">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search content... (Press / to focus)"
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onFocus={() => setIsSearchOpen(true)}
          className={cn(
            "w-full pl-10 pr-10 py-2 text-sm",
            "bg-gray-50 border border-gray-200 rounded-lg",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
            "dark:bg-gray-800 dark:border-gray-700 dark:text-white",
            "transition-colors duration-200"
          )}
        />
        {localQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isSearchOpen && (localQuery || searchResults.length > 0) && (
        <div
          ref={resultsRef}
          className={cn(
            "absolute top-full left-0 right-0 mt-1 z-50",
            "bg-white border border-gray-200 rounded-lg shadow-lg",
            "dark:bg-gray-800 dark:border-gray-700",
            "max-h-96 overflow-y-auto"
          )}
        >
          {isSearching ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span>Searching...</span>
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="py-2">
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              </div>
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className={cn(
                    "w-full px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700",
                    "border-b border-gray-100 dark:border-gray-700 last:border-b-0",
                    "transition-colors duration-150"
                  )}
                >
                  <div className="flex items-start space-x-3">
                    {result.thumbnail_url ? (
                      <img
                        src={result.thumbnail_url}
                        alt=""
                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded flex-shrink-0 flex items-center justify-center">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {result.title}
                      </p>
                      {result.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                          {result.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-blue-500 dark:text-blue-400">
                          {result.content_type}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-400">
                          {new Date(result.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : localQuery.trim() ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No results found for "{localQuery}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
